/**
 * Suppress React lifecycle deprecation warnings from third-party libraries
 * This filters out warnings from react-router, redux-form, and react-loadable
 * while keeping all other warnings and errors visible
 */

// Store the original console methods
const originalWarn = console.warn;
const originalError = console.error;
const originalLog = console.log;
const originalInfo = console.info;

// List of warning messages to suppress
const warningsToSuppress = [
    // React lifecycle warnings from third-party libraries
    'componentWillMount has been renamed',
    'componentWillReceiveProps has been renamed',
    'componentWillUpdate has been renamed',
    'UNSAFE_componentWillMount',
    'UNSAFE_componentWillReceiveProps',
    'UNSAFE_componentWillUpdate',
    // Moment.js deprecation warnings
    'Deprecation warning:',
    'moment construction falls back',
    'moment().subtract(period, number) is deprecated',
    'value provided is not in a recognized RFC2822 or ISO format',
    // React warnings from application code
    'Cannot update during an existing state transition',
    'Each child in a list should have a unique "key" prop',
    'validateDOMNesting',
    '<div> cannot appear as a child of <tbody>',
    'Render methods should be a pure function',
    // Other common third-party warnings
    '[Intervention]',
    'Slow network is detected',
    '[locatorjs]',
    '[HMR]',
    'Waiting for update signal'
];

// Override console.warn to filter out specific warnings
console.warn = function filterWarnings(...args) {
    const message = args[0];

    // Check if this is a string message that should be suppressed
    if (typeof message === 'string') {
        const shouldSuppress = warningsToSuppress.some(warning =>
            message.includes(warning)
        );

        if (shouldSuppress) {
            // Don't log this warning
            return;
        }
    }

    // Log all other warnings normally
    originalWarn.apply(console, args);
};

// Override console.error to filter out React lifecycle errors from third-party libs
console.error = function filterErrors(...args) {
    const message = args[0];

    // Check if this is a React lifecycle warning disguised as an error
    if (typeof message === 'string') {
        const shouldSuppress = warningsToSuppress.some(warning =>
            message.includes(warning)
        );

        if (shouldSuppress) {
            // Don't log this error
            return;
        }
    }

    // Log all other errors normally
    originalError.apply(console, args);
};

// Override console.log to filter out development tool messages
console.log = function filterLogs(...args) {
    const message = args[0];

    // Check if this is a development tool message that should be suppressed
    if (typeof message === 'string') {
        const shouldSuppress = warningsToSuppress.some(warning =>
            message.includes(warning)
        );

        if (shouldSuppress) {
            // Don't log this message
            return;
        }
    }

    // Log all other messages normally
    originalLog.apply(console, args);
};

// Override console.info to filter out intervention messages
console.info = function filterInfo(...args) {
    const message = args[0];

    // Check if this is an info message that should be suppressed
    if (typeof message === 'string') {
        const shouldSuppress = warningsToSuppress.some(warning =>
            message.includes(warning)
        );

        if (shouldSuppress) {
            // Don't log this info message
            return;
        }
    }

    // Log all other info messages normally
    originalInfo.apply(console, args);
};

// Export a function to restore original console methods if needed
export const restoreConsole = () => {
    console.warn = originalWarn;
    console.error = originalError;
    console.log = originalLog;
    console.info = originalInfo;
};

// Export a function to check if suppression is active
export const isSuppressionActive = () => {
    return console.warn !== originalWarn || console.error !== originalError || console.log !== originalLog || console.info !== originalInfo;
};
